import { combineReducers, configureStore } from '@reduxjs/toolkit';
import { setupListeners } from '@reduxjs/toolkit/query';
import type { Persistor } from 'redux-persist';
import {
  FLUSH,
  PAUSE,
  PERSIST,
  persistReducer,
  persistStore,
  PURGE,
  REGISTER,
  REHYDRATE,
} from 'redux-persist';
import {
  navigationPermissionsReducer,
  initialState as navigationPermissionsInitialState,
} from '@skynet/data-navigation';
import { preferencesReducer } from '@skynet/data-preferences';

import { listenerMiddleware } from 'src/store/middleware/listenerMiddleware';
import { postMiddleware, preMiddleware } from 'src/store/middleware/dynamicMiddleware';

import 'src/store/middleware/wsAccessValidationMiddleware';

import 'src/store/middleware/handleWsMessage';

import { sentryReduxEnhancer } from 'src/store/sentry-enhancers';
import { authApi, authTokenReducer } from '@skynet/data-auth-api';
import { profileApi } from '@skynet/data-profile';
import { rolesApi } from '@skynet/data-roles';
import { itemsApi } from '@skynet/data-items';
import { signupApi, signupReducer } from '@skynet/data-signup';
import { tenantsApi } from '@skynet/data-tenants';
import { usersApi } from '@skynet/data-users';
import { userTenantMembershipsApi } from '@skynet/data-user-tenant-memberships';

import { rtkQueryErrorLogger } from 'src/store/middleware/rtkQueryLogger';
import { dexColdWalletApi } from '@skynet/data-dex-cold-wallets';
import { dexTokenMapApi } from '@skynet/data-dex-token-map';

import { botCommandsApi } from '@skynet/data-bot-commands';
import { smmApi } from '@skynet/data-smm';
import { botMessagesApi } from '@skynet/data-bot-messages';
import { rtkDexApi } from 'src/store/DEX/rtkDexApi';
import { dexVaultsApi } from '@skynet/data-dex-vaults';
import { dexBlockchainsApi } from '@skynet/data-dex-blockchains';
import { dexExchangesApi } from '@skynet/data-dex-exchanges';
import { dexTransactionsApi } from '@skynet/data-dex-transactions';
import { dexAgentApi } from '@skynet/data-dex-agent';
import { dexAddVaultsApi } from '@skynet/data-dex-add-vaults';
import { dexTasksApi } from '@skynet/data-dex-tasks';
import { dexVaultDeployerApi } from '@skynet/data-dex-vault-deployer';

import dexReducer from 'src/store/DEX';

import loggerMiddleware from './middleware/logger';
import crashMiddleware from './middleware/crash';
import { wsMmMiddleware, wsMmReducer } from '@skynet/data-ws-mm';
import handleWsRequestMiddleware from './middleware/handleWsRequest';
import { wsEsMiddleware } from '@skynet/data-ws-es';
import { wsAssistantMiddleware, wsAssistantReducer } from '@skynet/data-ws-assistant';
import { wsAuthMiddleware, wsAuthReducer } from '@skynet/data-ws-auth';
import { wsSmmMiddleware, wsSmmReducer } from '@skynet/data-ws-smm';

import { themeReducer } from '@skynet/theme-feature';
import { intlReducer } from '@skynet/intl-feature';
import { initialState as serversInitialState, serversReducer } from '@skynet/data-servers';
import authReducer from './auth/slice';
import loansReducer from './loans/slice';

import reportsReducer, { initialState as reportsInitialState } from './shared/reports/slice';
import {
  balanceAlertRulesReducer,
  initialState as balanceAlertRulesInitialState,
} from '@skynet/data-balance-alert-rules';
import { rtkQueryErrorReducer, rtkQueryErrorInitialState } from '@skynet/data-rtk-query-error';
import { arbitrageReducer, initialState as arbitrageInitialState } from '@skynet/data-arbitrage';
import { esServerModeReducer, mmServerModeReducer } from '@skynet/data-server-mode';

import { apiKeysReducer, initialState as apiKeysInitialState } from '@skynet/data-api-keys';
import {
  subAccountsReducer,
  initialState as subAccountsInitialState,
} from '@skynet/data-sub-accounts';
import esTradingReducer from './trading';
import retainersReducer from './retainers/slice';

import { initSharedAccountsSlice } from '@skynet/data-shared-accounts';
import exchangesReducer, {
  initialState as exchangesInitialState,
} from './shared/exchange-list/slice';
import {
  exchangeSyncReducer,
  initialState as exchangeSyncInitialState,
} from '@skynet/data-exchange-sync';
import {
  tradingGroupJobsReducer,
  initialState as tradingGroupJobsInitialState,
} from '@skynet/data-trading-groups-jobs';
import balancesReducer, { initialState as balancesInitialState } from './shared/balances/slice';
import logsReducer, { initialState as logsInitialState } from './shared/log/slice';
import balanceHistoryReducer, {
  initialState as balanceHistoryInitialState,
} from './shared/balance-history/slice';
import transactionsReducer, {
  initialState as transactionsInitialState,
} from './shared/transactions/slice';
import arbitrageInfoReducer, {
  initialState as arbitrageInfoInitialState,
} from './shared/arbitrage-info/slice';
import tasksReducer from './shared/tasks/slice';
import calculatedThresholdsReducer from './shared/threshold/calculation';
import storage from 'src/store/storage';
import { resetStore, withResetState } from 'src/store/actions';
import wsEsReducer from './trading/slice';
import { startGlobalEffects } from 'src/store/global-effects';
import { startWsAssistantEffects } from 'src/store/ws-assistant-effects';
import { startWsAuthEffects } from 'src/store/ws-auth-effects';
import { startWsSmmEffects } from 'src/store/ws-smm-effects';
import { startSharedAccountsListen } from 'src/store/shared/accounts-listen';
import { exchangesApi } from '@skynet/data-exchanges';
import { rtkExchangesApi } from './shared/rtkExchangesApi';
import { bugReportsApi } from '@skynet/data-bug-reports';
import { telegramGroupsApi } from '@skynet/data-trading-groups';
import { telegramUsersApi } from '@skynet/data-telegram-users';
import { tradingBotsApi } from '@skynet/data-trading-bots';
import { regionsApi } from '@skynet/data-regions';
import { marketingCampaignsApi } from '@skynet/data-marketing-campaigns';
import { projectsApi } from '@skynet/data-projects';

export const rootPersistConfig = {
  key: 'root',
  version: 1,
  storage,
  whitelist: [], // things to persist
  // blacklist: [] // things to do not persist
};

export const authPersistConfig = {
  key: 'auth',
  version: 1,
  storage,
  blacklist: ['error', 'resAuth', 'res2FA', 'authDomain', 'isAuthedMM', 'isAuthedES'],
};

export const prefPersistConfig = {
  key: 'preferences',
  version: 1,
  storage,
};

export const calculatedThresholdsConfig = {
  key: 'calculatedThresholds',
  version: 1,
  storage,
  blacklist: ['calculatedThresholds'],
};

const accountsReducer = initSharedAccountsSlice(resetStore);

const reducers = combineReducers({
  // auth
  auth: persistReducer(authPersistConfig, authReducer),
  authToken: authTokenReducer,
  signup: signupReducer,
  navigationPermissions: withResetState(
    navigationPermissionsReducer,
    navigationPermissionsInitialState,
  ),

  // misc reducers
  theme: themeReducer,
  intl: intlReducer,
  preferences: persistReducer(prefPersistConfig, preferencesReducer),

  wsMm: wsMmReducer,
  mmServerMode: mmServerModeReducer,
  wsEs: wsEsReducer,
  esServerMode: esServerModeReducer,
  wsAssistant: wsAssistantReducer,
  wsAuth: wsAuthReducer,
  wsSmm: wsSmmReducer,

  calculatedThresholds: persistReducer(calculatedThresholdsConfig, calculatedThresholdsReducer),

  // shared
  accounts: accountsReducer,
  exchanges: withResetState(exchangesReducer, exchangesInitialState),
  exchangeSync: withResetState(exchangeSyncReducer, exchangeSyncInitialState),
  tradingGroupJobs: withResetState(tradingGroupJobsReducer, tradingGroupJobsInitialState),
  balances: withResetState(balancesReducer, balancesInitialState),
  logs: withResetState(logsReducer, logsInitialState),
  balanceHistory: withResetState(balanceHistoryReducer, balanceHistoryInitialState),
  transactions: withResetState(transactionsReducer, transactionsInitialState),
  arbitrageInfo: withResetState(arbitrageInfoReducer, arbitrageInfoInitialState),
  tasks: tasksReducer,
  reports: withResetState(reportsReducer, reportsInitialState),
  balanceAlertRules: withResetState(balanceAlertRulesReducer, balanceAlertRulesInitialState),

  // admin reducers
  admin: withResetState(serversReducer, serversInitialState),
  arbitrage: withResetState(arbitrageReducer, arbitrageInitialState),
  apiKeys: withResetState(apiKeysReducer, apiKeysInitialState),
  subAccounts: withResetState(subAccountsReducer, subAccountsInitialState),

  // loans reducers
  loans: loansReducer,

  // retainers reducers
  retainers: retainersReducer,

  // ES reducers
  esTrading: esTradingReducer,

  // DEX reducers
  dex: dexReducer,

  // RTK reducers
  rtkQueryError: withResetState(rtkQueryErrorReducer, rtkQueryErrorInitialState),
  [dexTokenMapApi.reducerPath]: dexTokenMapApi.reducer,
  [exchangesApi.reducerPath]: exchangesApi.reducer,
  [rtkExchangesApi.reducerPath]: rtkExchangesApi.reducer,
  [botCommandsApi.reducerPath]: botCommandsApi.reducer,
  [smmApi.reducerPath]: smmApi.reducer,
  [botMessagesApi.reducerPath]: botMessagesApi.reducer,
  [marketingCampaignsApi.reducerPath]: marketingCampaignsApi.reducer,
  [bugReportsApi.reducerPath]: bugReportsApi.reducer,
  [telegramGroupsApi.reducerPath]: telegramGroupsApi.reducer,
  [telegramUsersApi.reducerPath]: telegramUsersApi.reducer,
  [tradingBotsApi.reducerPath]: tradingBotsApi.reducer,
  [profileApi.reducerPath]: profileApi.reducer,
  [authApi.reducerPath]: authApi.reducer,
  [rolesApi.reducerPath]: rolesApi.reducer,
  [itemsApi.reducerPath]: itemsApi.reducer,
  [tenantsApi.reducerPath]: tenantsApi.reducer,
  [signupApi.reducerPath]: signupApi.reducer,
  [usersApi.reducerPath]: usersApi.reducer,
  [dexColdWalletApi.reducerPath]: dexColdWalletApi.reducer,
  [rtkDexApi.reducerPath]: rtkDexApi.reducer,
  [dexVaultsApi.reducerPath]: dexVaultsApi.reducer,
  [dexBlockchainsApi.reducerPath]: dexBlockchainsApi.reducer,
  [dexExchangesApi.reducerPath]: dexExchangesApi.reducer,
  [dexTransactionsApi.reducerPath]: dexTransactionsApi.reducer,
  [dexAgentApi.reducerPath]: dexAgentApi.reducer,
  [dexAddVaultsApi.reducerPath]: dexAddVaultsApi.reducer,
  [dexTasksApi.reducerPath]: dexTasksApi.reducer,
  [dexVaultDeployerApi.reducerPath]: dexVaultDeployerApi.reducer,
  [regionsApi.reducerPath]: regionsApi.reducer,
  [userTenantMembershipsApi.reducerPath]: userTenantMembershipsApi.reducer,
  [projectsApi.reducerPath]: projectsApi.reducer,
});

const persistedReducer = persistReducer(rootPersistConfig, reducers);

export interface ExtraArgument {
  persistor?: Persistor;
}

const extraArgument: ExtraArgument = {};

const getDefaultMiddlewareParams = {
  serializableCheck: {
    ignoredActions: [FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER],
    // ignoredActions: ['MM__WS::OPEN'],
    ignoredActionPaths: ['meta.arg', 'meta.timestamp', 'payload', 'meta.baseQueryMeta'],
  },
  thunk: {
    extraArgument,
  },
};

const middlewares = [
  // order matters!
  dexTokenMapApi.middleware,
  profileApi.middleware,
  authApi.middleware,
  rolesApi.middleware,
  itemsApi.middleware,
  tenantsApi.middleware,
  signupApi.middleware,
  usersApi.middleware,
  dexColdWalletApi.middleware,
  exchangesApi.middleware,
  rtkExchangesApi.middleware,
  botCommandsApi.middleware,
  smmApi.middleware,
  botMessagesApi.middleware,
  marketingCampaignsApi.middleware,
  bugReportsApi.middleware,
  telegramGroupsApi.middleware,
  telegramUsersApi.middleware,
  tradingBotsApi.middleware,
  rtkDexApi.middleware,
  dexVaultsApi.middleware,
  dexBlockchainsApi.middleware,
  dexExchangesApi.middleware,
  dexTransactionsApi.middleware,
  dexAgentApi.middleware,
  dexAddVaultsApi.middleware,
  dexTasksApi.middleware,
  dexVaultDeployerApi.middleware,
  regionsApi.middleware,
  userTenantMembershipsApi.middleware,
  projectsApi.middleware,

  preMiddleware.middleware,
  // handleWsAccessValidationMiddleware,

  crashMiddleware,
  loggerMiddleware,
  wsMmMiddleware,
  wsEsMiddleware,
  wsAssistantMiddleware,
  wsAuthMiddleware,
  wsSmmMiddleware,

  handleWsRequestMiddleware,
  postMiddleware.middleware,
  // handleWsMessageMiddleware,

  // loan and retainer middleware
  listenerMiddleware.middleware,
  rtkQueryErrorLogger,
];

export const store = configureStore({
  reducer: persistedReducer,
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware(getDefaultMiddlewareParams).concat(middlewares),
  enhancers: (getDefaultEnhancers) => getDefaultEnhancers().concat(sentryReduxEnhancer),
});

export function setupStore(preloadedState?: RootState) {
  return configureStore({
    reducer: persistedReducer,
    preloadedState,
    middleware: (getDefaultMiddleware) =>
      getDefaultMiddleware(getDefaultMiddlewareParams).concat(middlewares),
  });
}

startGlobalEffects();
startWsAssistantEffects();
startWsAuthEffects();
startWsSmmEffects();
startSharedAccountsListen();

// Dispatches RTK Query's focus/reconnect actions. Only queries that opt in
// (`refetchOnFocus` / `refetchOnReconnect`, both off by default) react to
// them, e.g. the SMM post lists, whose thumbnail links expire.
setupListeners(store.dispatch);

export const persistor = persistStore(store);
extraArgument.persistor = persistor;

// Infer the `RootState` and `AppDispatch` types from the store itself
export type RootState = ReturnType<typeof store.getState>;
// export type AppStore = ReturnType<typeof setupStore>;
export type AppStore = typeof store;

// Inferred type: {posts: PostsState, comments: CommentsState, users: UsersState}
// export type AppDispatch = AppStore['dispatch'];
export type AppDispatch = typeof store.dispatch;
